import * as React from 'react';
import Downloader, { IDownloader, DownloadProgress } from './Downloader';

export interface FileDownloaderProps {
    /**
     * The URL to download
     */
    url: string;
    /**
     * The render function that will render when the content is downloaded
     * @returns {JSX.Element}
     */
    renderContent: (content: string) => JSX.Element;
    /**
     * An optional function to render progress (defaults to basic text display of progress)
     * @param {DownloadProgress} progress
     * @returns {JSX.Element}
     */
    renderProgress?: (progress: DownloadProgress) => JSX.Element;
    /**
     * An optional function to render errors (defaults to basic text display of errors)
     * @param {string} msg
     * @param {Error} error
     * @returns {JSX.Element}
     */
    renderError?: (msg: string, error?: Error) => JSX.Element;
    /**
     * A <key>:<Downloader> map for Downloader Component
     */
    protocolHandlers: { [key: string]: IDownloader };
}

export const FileDownloader = (props: FileDownloaderProps): JSX.Element => {
    const [content, setContent] = React.useState<string>();
    const [progress, setProgress] = React.useState<DownloadProgress>({
        message: `Downloading ${props.url}...`,
        bytesDownloaded: 0,
    });
    const [error, setError] = React.useState<string>();
    const {
        url,
        renderContent,
        renderProgress,
        renderError = (msg: string): JSX.Element => <div>{msg}</div>,
    } = props;
    React.useEffect(() => {
        const downloader = new Downloader(props.protocolHandlers);
        downloader.downloadWithProgress(
            url,
            complete => setContent(complete), // done downloading
            (currentProgress: DownloadProgress): void => {
                setProgress(currentProgress);
            },
            (msg: string): void => {
                setError(msg);
            }
        );
    }, [props.protocolHandlers, url]);
    if (content) {
        return renderContent(content);
    }
    if (error) {
        return renderError(error);
    }
    return typeof renderProgress === 'function' ? renderProgress(progress) : null;
};
